import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { CountryToken, CountryRegionToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FormField } from '~/components/system/form-field';
import { applyServerValidation, serverMessage } from '~/lib/api/server-error';
import { usePostalCodeMutations } from '~/hooks/use-postal-codes';
import { postalCodeSchema, EMPTY_POSTAL_CODE, type PostalCodeFormValues } from '~/lib/schemas/postal-code';

const NONE = '__none__';

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	initial: PostalCodeFormValues | null;
	countries: CountryToken[];
	regions: CountryRegionToken[];
}

/** Add / edit a postal code (code + optional country/region + area/city). */
export function PostalCodeEditDialog({ open, onOpenChange, initial, countries, regions }: Props) {
	const { t } = useTranslation();
	const { create, update } = usePostalCodeMutations();
	const form = useForm<PostalCodeFormValues>({
		resolver: zodResolver(postalCodeSchema) as Resolver<PostalCodeFormValues>,
		defaultValues: initial ?? EMPTY_POSTAL_CODE,
		mode: 'onChange'
	});
	const { register, handleSubmit, reset, setError, setValue, watch, formState } = form;
	const isEdit = Boolean(initial?.id);

	useEffect(() => {
		if (open) reset(initial ?? EMPTY_POSTAL_CODE);
	}, [open, initial, reset]);

	const onSubmit = handleSubmit(async values => {
		try {
			if (values.id) await update.mutateAsync(values);
			else await create.mutateAsync(values);
			toast.success(t('common.save'));
			onOpenChange(false);
		} catch (err) {
			if (!applyServerValidation(err, setError)) toast.error(serverMessage(err));
		}
	});

	const cid = watch('cid');
	const rid = watch('rid');
	const visible = watch('iv');
	// Only show regions belonging to the selected country (or all when no country chosen).
	const regionOptions = cid == null ? regions : regions.filter(r => r.cid === cid);

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{isEdit ? t('system.postalCodes.editTitle') : t('system.postalCodes.addTitle')}</DialogTitle>
					<DialogDescription>{t('system.postalCodes.formSubtitle')}</DialogDescription>
				</DialogHeader>
				<form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
					<div className="grid grid-cols-2 gap-4">
						<FormField label={t('system.postalCodes.columns.code')} error={formState.errors.cd?.message}>
							<Input {...register('cd')} maxLength={20} aria-invalid={Boolean(formState.errors.cd)} autoFocus />
						</FormField>
						<FormField label={t('system.postalCodes.columns.sort')} error={formState.errors.si?.message}>
							<Input type="number" {...register('si', { valueAsNumber: true })} aria-invalid={Boolean(formState.errors.si)} />
						</FormField>
					</div>
					<div className="grid grid-cols-2 gap-4">
						<FormField label={t('system.postalCodes.columns.country')}>
							<Select
								value={cid === null ? NONE : String(cid)}
								onValueChange={v => {
									setValue('cid', v === NONE ? null : Number(v), { shouldDirty: true });
									setValue('rid', null, { shouldDirty: true });
								}}>
								<SelectTrigger className="w-full" aria-label={t('system.postalCodes.columns.country')}>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value={NONE}>{t('system.postalCodes.none')}</SelectItem>
									{countries.map(c => (
										<SelectItem key={c.id} value={String(c.id)}>
											{c.nm}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</FormField>
						<FormField label={t('system.postalCodes.columns.region')}>
							<Select
								value={rid === null ? NONE : String(rid)}
								onValueChange={v => setValue('rid', v === NONE ? null : Number(v), { shouldDirty: true })}>
								<SelectTrigger className="w-full" aria-label={t('system.postalCodes.columns.region')}>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value={NONE}>{t('system.postalCodes.none')}</SelectItem>
									{regionOptions.map(r => (
										<SelectItem key={r.id} value={String(r.id)}>
											{r.nm}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</FormField>
					</div>
					<div className="grid grid-cols-2 gap-4">
						<FormField label={t('system.postalCodes.columns.area')} error={formState.errors.ar?.message}>
							<Input {...register('ar')} maxLength={100} aria-invalid={Boolean(formState.errors.ar)} />
						</FormField>
						<FormField label={t('system.postalCodes.columns.city')} error={formState.errors.ct?.message}>
							<Input {...register('ct')} maxLength={100} aria-invalid={Boolean(formState.errors.ct)} />
						</FormField>
						<FormField label={t('system.postalCodes.columns.district')} error={formState.errors.dst?.message}>
							<Input {...register('dst')} maxLength={100} aria-invalid={Boolean(formState.errors.dst)} />
						</FormField>
					</div>
					<div className="flex items-center justify-between rounded-md border border-border px-4 py-3">
						<Label htmlFor="postal-visible">{t('system.postalCodes.columns.visible')}</Label>
						<Switch id="postal-visible" checked={visible} onCheckedChange={next => setValue('iv', next, { shouldDirty: true })} />
					</div>
					<DialogFooter>
						<Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
							{t('common.cancel')}
						</Button>
						<Button type="submit" disabled={create.isPending || update.isPending}>
							{create.isPending || update.isPending ? t('common.saving') : t('common.save')}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
